import type React from "react";
import { useCallback } from "react";

import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import type { OpenAPI } from "simplydo/interfaces";
import { styled } from "simplydo/react/styled";
import { Icon, Loader, Menu, Pagination } from "simplydo/react/ui";

import api from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import { EmptyBox } from "components/lib/UI";

import NotificationItem from "./NotificationItems";
import { useTrayStatus } from "./index";
import type { AnnouncementNotification, AppNotification } from "./types";

type NotificationTrayProps = {
  page: number;
  total: number;
  loading: boolean;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  invitations: OpenAPI.GET<`/invitations`>["response"]["invitations"];
  acceptableInvitations: OpenAPI.GET<`/invitations`>["response"]["invitations"];
  notifications: AppNotification[];
  setAnnouncementNotification: React.Dispatch<
    React.SetStateAction<AnnouncementNotification | null>
  >;
  setAnnouncementModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

export const StyledPagincationContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 7px;
`;

const StyledContent = styled.div`
  margin: 0 !important;
  min-width: ${({ theme }) =>
    theme.sizes.isMobile ? "100%" : "500px"} !important;
  max-height: ${({ theme }) =>
    theme.sizes.isMobile ? null : "500px"} !important;
  overflow: auto;
`;

const Notifications = ({
  acceptableInvitations,
  invitations,
  loading,
  notifications,
  page,
  setPage,
  total,
  setAnnouncementNotification,
  setAnnouncementModalOpen,
}: NotificationTrayProps) => {
  const { t } = useTranslation();
  const user = useAppSelector((state) => state.user);
  const [_, setTrayStatus] = useTrayStatus();

  const onInvitationHandled = useCallback((invitationId: string) => {
    api.setQueryData(api.invitations.get, [], (prev) => ({
      ...prev,
      invitations: (prev?.invitations || []).filter(
        (i) => i._id !== invitationId,
      ),
    }));
  }, []);

  const markAsInteracted = useCallback(
    (notificationId: string) => {
      setTrayStatus({ open: false });
      api.notifications.markAsInteracted(
        notificationId,
        () => {
          api.setQueryData(api.notifications.get, [], (prev) => ({
            ...prev,
            notifications: (prev?.notifications || []).map((notification) =>
              notification._id === notificationId ?
                { ...notification, interacted: true }
              : notification,
            ),
          }));
        },
        () => {},
      );
    },
    [setTrayStatus],
  );

  return (
    <div style={{ maxHeight: 600 }}>
      {loading ?
        <Loader active />
      : null}
      {!loading && notifications.length ?
        <>
          <StyledContent>
            {!loading && acceptableInvitations.length ?
              <Menu.Item
                style={{
                  borderBottom: "1px solid",
                  borderColor: "#E9EBEE",
                  padding: ".833em 1em",
                  borderRadius: 0,
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  textOverflow: "ellipsis",
                  marginTop: 5,
                  marginBottom: 5,
                  width: "99%",
                  gap: 10,
                  color: "black",
                }}
                fitted
                link
                as={Link}
                to="/invitations"
              >
                <Icon
                  name="paper plane"
                  size="big"
                  style={{ width: 50, margin: 0 }}
                />
                <div>
                  <h4>Invitations</h4>
                  <div>
                    There{" "}
                    {util.pluralise(
                      acceptableInvitations.length,
                      "is",
                      "are",
                      false,
                    )}{" "}
                    {util.pluralise(
                      acceptableInvitations.length,
                      "invitation",
                      "invitations",
                    )}{" "}
                    waiting for you.
                  </div>
                </div>
              </Menu.Item>
            : null}
            {notifications.map((notification) => (
              <>
                <NotificationItem
                  setAnnouncementNotification={setAnnouncementNotification}
                  setAnnouncementModalOpen={setAnnouncementModalOpen}
                  notification={notification}
                  user={user}
                  key={notification._id}
                  markNotificationAsInteracted={markAsInteracted}
                  invitations={invitations}
                  onInvitationHandled={onInvitationHandled}
                />
              </>
            ))}
          </StyledContent>
          <StyledPagincationContainer>
            <Pagination
              totalPages={Math.ceil(total / 30)}
              activePage={page}
              ellipsisItem={null}
              firstItem={null}
              lastItem={null}
              onPageChange={(_e, { activePage }) =>
                setPage(activePage as number)
              }
            />
          </StyledPagincationContainer>
        </>
      : null}
      {!loading && !notifications.length ?
        <div>
          <EmptyBox>
            <h5>{t("notifications.empty")}</h5>
          </EmptyBox>
        </div>
      : null}
    </div>
  );
};

export default Notifications;
